import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, ListGroup, Spinner } from "react-bootstrap";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import { toast } from "react-toastify";
import axios from "axios";

const DURATION_UNITS = ["H", "Years", "Months", "Weeks"];
const LABOUR_TYPES = ["SUP", "MAN", "LAB"];
const LABOUR_DURATION_UNITS = ["H", "Days", "Weeks", "Months", "Years"];

const PorductionPlan = () => {
  const { user } = useAuth();
  const orgId = user?.organisationId;

  const [loading, setLoading] = useState(false);
  const [fetchingLookups, setFetchingLookups] = useState(false);
  const [processes, setProcesses] = useState([]);
  const [items, setItems] = useState([]);
  const [AllItems,setAllItems]=useState([]);
  const [units, setUnits] = useState([]);
  const [offices, setOffices] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  // top-level form
  const [form, setForm] = useState({
    planName: "",
    itemCode: "", // top-level item code (now will be an itemCode string from items)
    productionQuantity: "",
    productionQuantityUnit: "",
    officeCode: "",
  });

  // helpers to create empty objects
  const emptyLabour = () => ({
    labourType: LABOUR_TYPES[0],
    labourDuration: "",
    labourDurationUnit: LABOUR_DURATION_UNITS[0],
  });

  const emptyProcessItem = () => ({
    itemCodeId: "",
    bom: null,
    bomLoading: false,
    bomError: null,
  });

  // New: subprocess structure for each subId
  const emptySubprocessEntry = () => ({
    items: [emptyProcessItem()], // items array
    approxDuration: "",
    approxDurationUnit: DURATION_UNITS[0],
    labours: [emptyLabour()],
    // SFG output fields requested
    sfgOutputItem: "",
    sfgOutputQty: "",
    sfgOutputUnit: "",
  });

  // process entry now includes a map of subId -> subprocessEntry
  const emptyProcessEntry = () => ({
    productionProcessId: "",
    // subprocessItems: { subId: { items: [...], approxDuration, approxDurationUnit, labours: [...], sfgOutputItem, sfgOutputQty, sfgOutputUnit } }
    subprocessItems: {},
  });

  const [processEntries, setProcessEntries] = useState([emptyProcessEntry()]);

  // --- fetch lookups on mount ---
  useEffect(() => {
    if (!orgId) return;
    const fetchAllLookups = async () => {
      setFetchingLookups(true);
      setFetchError(null);
      try {
        // processes
        const pRes = await fetch(`${Base_URL}/ProductionStation/GetAllProcesses/id?id=${encodeURIComponent(orgId)}`);
        if (!pRes.ok) throw new Error(`Failed to load processes: ${pRes.status}`);
        const pData = await pRes.json();
        const pArr = Array.isArray(pData) ? pData : [];
        setProcesses(pArr);

        // items
        const iRes = await fetch(`${Base_URL}/Item/ItemGetData`);
        if (!iRes.ok) throw new Error(`Failed to load items: ${iRes.status}`);
        const iData = await iRes.json();
        const filteredItems = Array.isArray(iData) ? iData.filter(it => it.itemCode && String(it.itemCode).startsWith("RA")) : [];
        setItems(filteredItems);
        setAllItems(iData);

        // units
        const uRes = await fetch(`${Base_URL}/UnitMaster/GetAllUnits`);
        if (!uRes.ok) throw new Error(`Failed to load units: ${uRes.status}`);
        const uData = await uRes.json();
        const uArr = Array.isArray(uData) ? uData : [];
        setUnits(uArr);

        // offices
        let officeData = [];
        try {
          const officeRes = await fetch(`${Base_URL}/Office/OfficeByOrgId/${encodeURIComponent(orgId)}`);
          if (officeRes.ok) {
            const od = await officeRes.json();
            officeData = Array.isArray(od) ? od : [];
          } else {
            const officeRes2 = await fetch(`${Base_URL}/Office/OfficeByOrgId?orgId=${encodeURIComponent(orgId)}`);
            if (officeRes2.ok) {
              const od2 = await officeRes2.json();
              officeData = Array.isArray(od2) ? od2 : [];
            }
          }
        } catch (e) {
          console.warn("Office fetch fallback attempt failed", e);
        }
        setOffices(officeData);

        // defaults
        setForm(prev => ({
          ...prev,
          productionQuantityUnit: uArr[0]?.unitCode ?? uArr[0]?.code ?? uArr[0]?.value ?? prev.productionQuantityUnit ?? "",
          officeCode: officeData[0]?.officeCode ?? officeData[0]?.code ?? officeData[0]?.value ?? prev.officeCode ?? "",
        }));

        // default first process id into first entry if available
        if (pArr.length > 0) {
          setProcessEntries(prev => {
            const copy = [...prev];
            if (!copy[0].productionProcessId) {
              copy[0].productionProcessId = String(pArr[0].productionProcessId ?? pArr[0].productionprocessid ?? pArr[0].id ?? "");
              // initialize subprocessItems for entry 0 as map of subId -> emptySubprocessEntry()
              const subs = parseSubprocessList(pArr[0]);
              const map = {};
              subs.forEach(sid => map[sid] = emptySubprocessEntry());
              copy[0].subprocessItems = map;
            }
            return copy;
          });
        }

      } catch (err) {
        console.error(err);
        setFetchError(err.message ?? "Error loading lookups");
      } finally {
        setFetchingLookups(false);
      }
    };

    fetchAllLookups();
  }, [orgId]);

  // --- helpers ---
  const findProcessById = (id) => {
    if (!id) return null;
    return processes.find(p => String(p.productionProcessId ?? p.productionprocessid ?? p.id) === String(id)) ?? null;
  };

  const findItemById = (id) => {
    if (!id) return null;
    return items.find(it => String(it.itemCodeId) === String(id)) ?? null;
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleDateString();
    } catch {
      return iso;
    }
  };

  const parseSubprocessList = (proc) => {
    const raw = proc?.subprocessList ?? proc?.subProcessList ?? "";
    if (!raw) return [];
    return String(raw).split(",").map(s => s.trim()).filter(Boolean);
  };

  // --- handle top-level change ---
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // --- process entries CRUD ---
  const addProcessEntry = () => {
    const defaultProc = processes[0]?.productionProcessId ?? processes[0]?.productionprocessid ?? processes[0]?.id ?? "";
    setProcessEntries(prev => [...prev, { ...emptyProcessEntry(), productionProcessId: defaultProc ? String(defaultProc) : "" }]);
  };
  const removeProcessEntry = (index) => {
    setProcessEntries(prev => prev.filter((_, i) => i !== index));
  };

  const updateProcessEntry = (index, patch) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], ...patch };
      return copy;
    });
  };

  // --- when process selection changes: initialize subprocessItems map for that entry ---
  const onProcessSelectChange = (index, value) => {
    const proc = findProcessById(value);
    const subs = parseSubprocessList(proc || {});
    const map = {};
    subs.forEach(sid => map[sid] = emptySubprocessEntry());
    updateProcessEntry(index, { productionProcessId: value, subprocessItems: map });
  };

  // --- subprocess-level operations ---
  // add item to subprocess.items array
  const addItemToSubprocess = (procIndex, subId) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const entry = { ...copy[procIndex] };
      const map = { ...(entry.subprocessItems || {}) };
      const sub = { ...(map[subId] || emptySubprocessEntry()) };
      sub.items = [...(sub.items || []), emptyProcessItem()];
      map[subId] = sub;
      entry.subprocessItems = map;
      copy[procIndex] = entry;
      return copy;
    });
  };

  // remove item from subprocess.items
  const removeItemFromSubprocess = (procIndex, subId, itemIndex) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const entry = { ...copy[procIndex] };
      const map = { ...(entry.subprocessItems || {}) };
      const sub = { ...(map[subId] || emptySubprocessEntry()) };
      sub.items = (sub.items || []).filter((_, i) => i !== itemIndex);
      if (!sub.items || sub.items.length === 0) sub.items = [emptyProcessItem()]; // keep at least one
      map[subId] = sub;
      entry.subprocessItems = map;
      copy[procIndex] = entry;
      return copy;
    });
  };

  // update a field of the subprocess itself (approxDuration, labours array, sfg fields)
  const updateSubprocessField = (procIndex, subId, patch) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const entry = { ...copy[procIndex] };
      const map = { ...(entry.subprocessItems || {}) };
      const sub = { ...(map[subId] || emptySubprocessEntry()) };
      Object.assign(sub, patch);
      map[subId] = sub;
      entry.subprocessItems = map;
      copy[procIndex] = entry;
      return copy;
    });
  };

  // update a specific item in subprocess.items (patch applies to that item)
  const updateSubprocessItem = (procIndex, subId, itemIndex, patch) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const entry = { ...copy[procIndex] };
      const map = { ...(entry.subprocessItems || {}) };
      const sub = { ...(map[subId] || emptySubprocessEntry()) };
      sub.items = (sub.items || []).map((it, i) => i === itemIndex ? { ...it, ...patch } : it);
      map[subId] = sub;
      entry.subprocessItems = map;
      copy[procIndex] = entry;
      return copy;
    });
  };

  // Fetch BOM for a subprocess item (same as before but wired to new structure)
  const fetchBOMForSubprocessItem = async (procIndex, subId, itemIndex, itemCodeId) => {
    if (!itemCodeId) {
      updateSubprocessItem(procIndex, subId, itemIndex, { bom: null, bomLoading: false, bomError: null });
      return;
    }
    updateSubprocessItem(procIndex, subId, itemIndex, { bomLoading: true, bomError: null });
    try {
      const res = await fetch(`${Base_URL}/ProductionStation/GetBOMfromItem/id?id=${encodeURIComponent(itemCodeId)}`);
      if (!res.ok) throw new Error(`BOM fetch failed: ${res.status}`);
      const data = await res.json();
      updateSubprocessItem(procIndex, subId, itemIndex, { bom: data || null, bomLoading: false, bomError: null });
    } catch (err) {
      console.error("Error fetching BOM", err);
      updateSubprocessItem(procIndex, subId, itemIndex, { bom: null, bomLoading: false, bomError: err.message || "Error loading BOM" });
    }
  };

  // --- labour operations per-subprocess (moved from per-process) ---
  const addLabourToSubprocess = (procIndex, subId) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const entry = { ...copy[procIndex] };
      const map = { ...(entry.subprocessItems || {}) };
      const sub = { ...(map[subId] || emptySubprocessEntry()) };
      sub.labours = [...(sub.labours || []), emptyLabour()];
      map[subId] = sub;
      entry.subprocessItems = map;
      copy[procIndex] = entry;
      return copy;
    });
  };

  const removeLabourFromSubprocess = (procIndex, subId, labourIndex) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const entry = { ...copy[procIndex] };
      const map = { ...(entry.subprocessItems || {}) };
      const sub = { ...(map[subId] || emptySubprocessEntry()) };
      sub.labours = (sub.labours || []).filter((_, i) => i !== labourIndex);
      if (!sub.labours || sub.labours.length === 0) sub.labours = [emptyLabour()];
      map[subId] = sub;
      entry.subprocessItems = map;
      copy[procIndex] = entry;
      return copy;
    });
  };

  const updateLabourInSubprocess = (procIndex, subId, labourIndex, patch) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const entry = { ...copy[procIndex] };
      const map = { ...(entry.subprocessItems || {}) };
      const sub = { ...(map[subId] || emptySubprocessEntry()) };
      sub.labours = (sub.labours || []).map((l, i) => i === labourIndex ? { ...l, ...patch } : l);
      map[subId] = sub;
      entry.subprocessItems = map;
      copy[procIndex] = entry;
      return copy;
    });
  };

  // --- when user selects an item inside a subprocess item row ---
  const onSubprocessItemSelect = (procIndex, subId, itemIndex, itemCodeId) => {
    updateSubprocessItem(procIndex, subId, itemIndex, { itemCodeId, bom: null, bomError: null });
    fetchBOMForSubprocessItem(procIndex, subId, itemIndex, itemCodeId);
  };

  // --- validation (moved process-level approx & labours to subprocess-level validations) ---
  const validate = () => {
    if (!form.planName || !form.planName.trim()) return "Enter Plan Name";
    if (!form.itemCode || !form.itemCode.trim()) return "Enter Item Code";
    if (!form.productionQuantity || isNaN(Number(form.productionQuantity))) return "Enter Production Quantity";
    if (!form.productionQuantityUnit) return "Select Production Quantity Unit";
    if (!form.officeCode) return "Select Office Code";
    if (!processEntries || processEntries.length === 0) return "Add at least one process";

    for (let i = 0; i < processEntries.length; i++) {
      const pe = processEntries[i];
      if (!pe.productionProcessId) return `Process ${i + 1}: select production process`;

      const map = pe.subprocessItems || {};
      const subIds = Object.keys(map || {});
      if (!subIds || subIds.length === 0) return `Process ${i + 1}: no subprocesses available for selected process`;

      for (let s = 0; s < subIds.length; s++) {
        const sid = subIds[s];
        const sub = map[sid] || emptySubprocessEntry();
        // approx duration for subprocess
        if (sub.approxDuration === "" || sub.approxDuration === null) return `Process ${i + 1} Subprocess ${sid}: enter approx duration`;
        if (!sub.approxDurationUnit) return `Process ${i + 1} Subprocess ${sid}: select approx duration unit`;

        // items - at least one
        const allItems = sub.items || [];
        if (!allItems || allItems.length === 0) return `Process ${i + 1} Subprocess ${sid}: add at least one item`;
        for (let j = 0; j < allItems.length; j++) {
          if (!allItems[j].itemCodeId) return `Process ${i + 1} Subprocess ${sid}: all items must have an item selected`;
        }

        // labours at subprocess level
        if (!sub.labours || sub.labours.length === 0) return `Process ${i + 1} Subprocess ${sid}: add at least one labour`;
        for (let k = 0; k < sub.labours.length; k++) {
          const lb = sub.labours[k];
          if (!lb.labourType) return `Process ${i + 1} Subprocess ${sid} Labour ${k + 1}: select labour type`;
          if (lb.labourDuration === "" || lb.labourDuration === null) return `Process ${i + 1} Subprocess ${sid} Labour ${k + 1}: enter labour duration`;
          if (!lb.labourDurationUnit) return `Process ${i + 1} Subprocess ${sid} Labour ${k + 1}: select labour duration unit`;
        }

        // (optional) SFG fields validation - if needed you can add required checks here
      }
    }
    return null;
  };

  // --- submit ---
  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    const payload = {
      planName: form.planName.trim(),
      itemCode: form.itemCode.trim(),
      orgId,
      productionQuantity: Number(form.productionQuantity),
      productionQuantityUnit: form.productionQuantityUnit,
      officeCode: form.officeCode,
      createdBy: user?.userId,
      processes: processEntries.map(pe => {
        const procDetails = findProcessById(pe.productionProcessId);
        // transform subprocessItems map into array [{ subProcessId, approxDuration, approxDurationUnit, labours: [...], sfgOutputItem, sfgOutputQty, sfgOutputUnit, items: [...] }]
        const subprocessArray = Object.entries(pe.subprocessItems || {}).map(([subId, sub]) => ({
          subProcessId: subId,
          approxDuration: Number(sub.approxDuration),
          approxDurationUnit: sub.approxDurationUnit,
          labours: (sub.labours || []).map(l => ({
            labourType: l.labourType,
            labourDuration: Number(l.labourDuration),
            labourDurationUnit: l.labourDurationUnit
          })),
          sfgOutputItem: sub.sfgOutputItem ?? "",
          sfgOutputQty: sub.sfgOutputQty ? Number(sub.sfgOutputQty) : null,
          sfgOutputUnit: sub.sfgOutputUnit ?? "",
          items: (sub.items || []).map(it => ({
            itemCodeId: it.itemCodeId,
            itemDetails: findItemById(it.itemCodeId) ?? null,
            bom: it.bom ?? null
          }))
        }));

        return {
          productionProcessId: pe.productionProcessId,
          productionProcessDetails: procDetails ?? null,
          subprocesses: procDetails ? parseSubprocessList(procDetails) : [],
          subprocessItems: subprocessArray,
        };
      })
    };

    try {
      setLoading(true);
      const res = await axios.post(`${Base_URL}/ProductionStation/CreatePlan`, payload);
      if (res.status === 200 || res.status === 201) {
        toast.success("Production plan created");
      } else {
        toast.error("Error creating production plan");
      }
    } catch (error) {
      console.error("CreatePlan error:", error);
      toast.error("Error: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
    console.log(payload);
  };

  // --- UI ---
  return (
    <div className="main-content expand">
      <Container fluid className="fields-container">
        <h3 className="fields-title">Production Plan</h3>

        <Form onSubmit={handleSubmit} className="fields-form">
          {/* Header row */}
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Name of the Plan</Form.Label>
                <Form.Control name="planName" value={form.planName} onChange={handleFormChange} placeholder="Plan name" />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Item Code (Top-level)</Form.Label>
                {/* Changed from free-text to dropdown showing items from API */}
                <Form.Control
                  as="select"
                  name="itemCode"
                  value={form.itemCode}
                  onChange={handleFormChange}
                  disabled={fetchingLookups || items.length === 0}
                >
                  <option value="">Select Item</option>
                  {AllItems.map((itm) => (
                    <option key={itm.itemCodeId} value={itm.itemCodeId}>{itm.itemCode} - {itm.itemName}</option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Production Quantity</Form.Label>
                <Form.Control name="productionQuantity" value={form.productionQuantity} onChange={handleFormChange} placeholder="Qty" />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Unit</Form.Label>
                <Form.Control as="select" name="productionQuantityUnit" value={form.productionQuantityUnit} onChange={handleFormChange} disabled={fetchingLookups || units.length === 0}>
                  <option value="">Select Unit</option>
                  {units.map((u, i) => {
                    const code = u.unitCode ?? u.code ?? u.value ?? u.id ?? u.UnitCode ?? u.unit;
                    const label = u.unitName ?? u.name ?? u.label ?? String(code);
                    return <option key={i} value={String(code)}>{label}</option>;
                  })}
                </Form.Control>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Office Code</Form.Label>
                <Form.Control as="select" name="officeCode" value={form.officeCode} onChange={handleFormChange} disabled={fetchingLookups || offices.length === 0}>
                  <option value="">Select Office</option>
                  {offices.map((o, i) => {
                    const code = o.officeCode ?? o.code ?? o.value ?? o.id ?? o.OfficeCode ?? "";
                    const name = o.officeName ?? o.name ?? o.officeName ?? o.label ?? String(code);
                    return <option key={i} value={String(code)}>{`${String(code)} - ${name}`}</option>;
                  })}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          {/* For each process entry */}
          {processEntries.map((pe, pIdx) => {
            const proc = findProcessById(pe.productionProcessId);
            const subprocessIds = parseSubprocessList(proc || {});
            return (
              <div key={pIdx} className="border rounded p-3 mb-3">
                {/* Row 1 - process selector (removed process-level approx & labours) */}
                <Row className="align-items-end">
                  <Col md={11}>
                    <Form.Group className="mb-3">
                      <Form.Label>Production Process #{pIdx + 1}</Form.Label>
                      <Form.Control as="select" value={pe.productionProcessId} onChange={(e) => onProcessSelectChange(pIdx, e.target.value)} disabled={fetchingLookups || processes.length === 0}>
                        <option value="">Select Process</option>
                        {processes.map((p, i) => {
                          const id = p.productionProcessId ?? p.productionprocessid ?? p.id ?? `proc-${i}`;
                          const desc = p.productionProcessDesc ?? p.productionprocessdesc ?? "";
                          return <option key={`${id}-${i}`} value={String(id)}>{id} {desc ? `- ${desc}` : ""}</option>;
                        })}
                      </Form.Control>
                    </Form.Group>
                  </Col>

                  <Col md={1} className="text-end">
                    {processEntries.length > 1 && <Button variant="danger" onClick={() => removeProcessEntry(pIdx)}>Remove</Button>}
                  </Col>
                </Row>

                {/* Row 2: Process details & subprocess list */}
                <Row>
                  <Col md={6}>
                    <h6>Process Details</h6>
                    {proc ? (
                      <ListGroup>
                        <ListGroup.Item><strong>ID:</strong> {proc.productionProcessId}</ListGroup.Item>
                        <ListGroup.Item><strong>Description:</strong> {proc.productionProcessDesc}</ListGroup.Item>
                        <ListGroup.Item><strong>Organisation:</strong> {proc.organisationId}</ListGroup.Item>
                      </ListGroup>
                    ) : <div>No process selected</div>}
                  </Col>

                  <Col md={6}>
                    <h6>Subprocesses</h6>
                    {proc ? (
                      <ListGroup>
                        {subprocessIds.length === 0 ? (
                          <ListGroup.Item>No subprocesses listed</ListGroup.Item>
                        ) : subprocessIds.map(sp => <ListGroup.Item key={sp}>{sp}</ListGroup.Item>)}
                      </ListGroup>
                    ) : <div>No subprocesses</div>}
                  </Col>
                </Row>

                {/* Row 3: For each subprocess, show SFG, approx duration, items and BOMs and labours */}
                <Row className="mt-3">
                  <Col md={12}>
                    <h6>Subprocess Items</h6>
                    {subprocessIds.length === 0 ? <div>No subprocesses to add items to</div> : subprocessIds.map(subId => {
                      const sub = (pe.subprocessItems && pe.subprocessItems[subId]) || emptySubprocessEntry();
                      const itemsForSub = sub.items || [emptyProcessItem()];
                      return (
                        <div key={subId} className="mb-3 border rounded p-2">
                          <div style={{ marginBottom: 8, fontWeight: 600 }}>{subId}</div>

                          {/* New: SFG output row - changed to dropdown to show items */}
                          <Row className="mb-2 align-items-end">
                            <Col md={5}>
                              <Form.Group>
                                <Form.Label>SFG Output Item</Form.Label>
                                <Form.Control
                                  as="select"
                                  value={sub.sfgOutputItem}
                                  onChange={(e) => updateSubprocessField(pIdx, subId, { sfgOutputItem: e.target.value })}
                                  disabled={fetchingLookups || items.length === 0}
                                >
                                  <option value="">Select Item</option>
                                  {AllItems.map((itm) => (
                                    <option key={itm.itemCodeId} value={itm.itemCodeId}>{itm.itemCode} - {itm.itemName}</option>
                                  ))}
                                </Form.Control>
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group>
                                <Form.Label>SFG Qty</Form.Label>
                                <Form.Control type="number" step="any" value={sub.sfgOutputQty} onChange={(e) => updateSubprocessField(pIdx, subId, { sfgOutputQty: e.target.value })} placeholder="Qty" />
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>SFG Unit</Form.Label>
                                <Form.Control
                                  as="select"
                                  value={sub.sfgOutputUnit}
                                  onChange={(e) => updateSubprocessField(pIdx, subId, { sfgOutputUnit: e.target.value })}
                                  disabled={fetchingLookups || units.length === 0}
                                >
                                  <option value="">Select Unit</option>
                                  {units.map((u, i) => {
                                    const code = u.unitCode ?? u.code ?? u.value ?? u.id ?? u.UnitCode ?? u.unit;
                                    const label = u.unitName ?? u.name ?? u.label ?? String(code);
                                    return (
                                      <option key={i} value={String(code)}>
                                        {label}
                                      </option>
                                    );
                                  })}
                                </Form.Control>
                              </Form.Group>
                            </Col>

                          </Row>

                          {/* New: Approx duration for this subprocess */}
                          <Row className="mb-3 align-items-end">
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>Approx. Duration (Subprocess)</Form.Label>
                                <Form.Control type="number" step="any" value={sub.approxDuration} onChange={(e) => updateSubprocessField(pIdx, subId, { approxDuration: e.target.value })} placeholder="Duration" />
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group>
                                <Form.Label>Unit</Form.Label>
                                <Form.Control as="select" value={sub.approxDurationUnit} onChange={(e) => updateSubprocessField(pIdx, subId, { approxDurationUnit: e.target.value })}>
                                  {DURATION_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                </Form.Control>
                              </Form.Group>
                            </Col>
                          </Row>

                          {/* Items for this subprocess */}
                          {(itemsForSub || []).map((it, itIdx) => (
                            <div key={itIdx} className="border rounded p-2 mb-2">
                              <Row className="align-items-end">
                                <Col md={5}>
                                  <Form.Group className="mb-2">
                                    <Form.Label>Item #{itIdx + 1}</Form.Label>
                                    <Form.Control as="select" value={it.itemCodeId} onChange={(e) => onSubprocessItemSelect(pIdx, subId, itIdx, e.target.value)} disabled={items.length === 0 || fetchingLookups}>
                                      <option value="">Select Item</option>
                                      {items.map((itm) => (
                                        <option key={itm.itemCodeId} value={itm.itemCodeId}>{itm.itemCode} - {itm.itemName}</option>
                                      ))}
                                    </Form.Control>
                                  </Form.Group>
                                </Col>

                                <Col md={5}>
                                  <div style={{ fontSize: 13 }}>
                                    {it.bomLoading ? (<div><Spinner animation="border" size="sm" /> Loading BOM...</div>) :
                                     it.bomError ? (<div style={{ color: "red" }}>{it.bomError}</div>) :
                                     it.bom ? (
                                       <div>
                                         <div><strong>BOM ID:</strong> {it.bom.bomId}</div>
                                         <div><strong>Qty/Parent:</strong> {it.bom.quantityPerParent} {it.bom.unitCode}</div>
                                         <div><strong>Scrap %:</strong> {it.bom.scrapPercentage}</div>
                                         <div><strong>Effective:</strong> {fmtDate(it.bom.effectiveFromDate)} - {fmtDate(it.bom.effectiveToDate)}</div>
                                       </div>
                                     ) : (<div>No BOM loaded</div>)}
                                  </div>
                                </Col>

                                <Col md={2} className="text-end">
                                  { (itemsForSub.length > 1) ? (
                                    <Button variant="outline-danger" onClick={() => removeItemFromSubprocess(pIdx, subId, itIdx)}>-</Button>
                                  ) : null }
                                </Col>
                              </Row>
                            </div>
                          ))}

                          <div className="mb-3">
                            <Button size="sm" onClick={() => addItemToSubprocess(pIdx, subId)}>+ Add Item to {subId}</Button>
                          </div>

                          {/* Labours for this subprocess */}
                          <div>
                            <h6>Labours (Subprocess)</h6>
                            {(sub.labours || []).map((lb, lIdx) => (
                              <Row key={lIdx} className="align-items-end mb-2">
                                <Col md={4}>
                                  <Form.Group>
                                    <Form.Label>Labour Type</Form.Label>
                                    <Form.Control as="select" value={lb.labourType} onChange={(e) => updateLabourInSubprocess(pIdx, subId, lIdx, { labourType: e.target.value })}>
                                      {LABOUR_TYPES.map(l => <option key={l} value={l}>{l}</option>)}
                                    </Form.Control>
                                  </Form.Group>
                                </Col>

                                <Col md={4}>
                                  <Form.Group>
                                    <Form.Label>Labour Duration</Form.Label>
                                    <Form.Control type="number" step="any" value={lb.labourDuration} onChange={(e) => updateLabourInSubprocess(pIdx, subId, lIdx, { labourDuration: e.target.value })} />
                                  </Form.Group>
                                </Col>

                                <Col md={3}>
                                  <Form.Group>
                                    <Form.Label>Duration Unit</Form.Label>
                                    <Form.Control as="select" value={lb.labourDurationUnit} onChange={(e) => updateLabourInSubprocess(pIdx, subId, lIdx, { labourDurationUnit: e.target.value })}>
                                      {LABOUR_DURATION_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                    </Form.Control>
                                  </Form.Group>
                                </Col>

                                <Col md={1} className="text-end">
                                  {sub.labours.length > 1 && <Button variant="outline-danger" onClick={() => removeLabourFromSubprocess(pIdx, subId, lIdx)}>-</Button>}
                                </Col>
                              </Row>
                            ))}

                            <div>
                              <Button size="sm" onClick={() => addLabourToSubprocess(pIdx, subId)}>+ Add Labour</Button>
                            </div>
                          </div>

                        </div>
                      );
                    })}
                  </Col>
                </Row>

              </div>
            );
          })}

          <div className="d-flex gap-2 mb-3">
            <Button type="button" onClick={addProcessEntry}>+ Add Process</Button>
            <Button type="button" variant="outline-secondary" onClick={() => setProcessEntries([emptyProcessEntry()])}>Reset Processes</Button>
          </div>

          <div className="d-flex gap-2 mt-3">
            <Button type="submit" variant="primary" disabled={fetchingLookups || loading}>
              {loading ? "Saving..." : "Save Production Plan"}
            </Button>
          </div>

          { (fetchingLookups || loading) && <div style={{ marginTop: 12 }}><Spinner animation="border" /></div> }
          { fetchError && <div style={{ color: "red", marginTop: 12 }}>{fetchError}</div> }
        </Form>
      </Container>
    </div>
  );
};

export default PorductionPlan;
